import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LABEL_STOCKS, useLabelStock } from '@/utils/labelStocks';
import { printPdf } from '@/utils/printPdf';
import { QR } from '@/components/QR';
import RoomPicker from '@/components/rooms/RoomPicker';
import { loadTenantBrand } from '@/components/tools/shared/tenantBrand';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * MT-2 — the label, as it will be stuck on the carton.
 *
 * One column, largest thing first: what it is, where it goes, the code that
 * opens the rest. The stylesheet lives in public/ because a 29 mm tape needs
 * 6px type and the design-token floor is 9px — these are stock sizes on film,
 * not screen type.
 */
export const TagLabel = ({ tag, stock, firm, project, projectCode }) => {
  const head = (
    <>
      <div className="mark">
        {firm.logoUrl ? <img src={firm.logoUrl} alt="" /> : null}
        <span className="firm">{firm.companyName}</span>
      </div>
      <div className="proj">{project}</div>
      {projectCode ? <div className="pno">{projectCode}</div> : null}
      <div className="room">
        {[tag.room, tag.location].filter(Boolean).join(' · ')}
      </div>
      {/* PR-1c — the preview says what the face says: where it is standing. */}
      {tag.at_where ? (
        <div className="foot" data-testid={`tag-label-at-${tag.code}`}>
          <span>{`AT: ${[tag.at_where, tag.at_location].filter(Boolean).join(' · ')}`.toUpperCase()}</span>
        </div>
      ) : null}
      <div className="tagname">{tag.tag_name || tag.title}</div>
      <div className="item">{tag.title}</div>
      {tag.spec_num ? <div className="spec">{tag.spec_num}</div> : null}
      {tag.instructions ? <div className="note">{tag.instructions}</div> : null}
    </>
  );
  const foot = (
    <div className="foot">
      <span>{[tag.trade_prefix, tag.trade].filter(Boolean).join(' · ')}</span>
      {/* SP-2 — a box says what it carries; a single item says which it is. */}
      <span><b>{tag.package_count
        ? `${tag.package_count} of ${tag.count_total}`
        : `${tag.count_index} of ${tag.count_total}`}</b></span>
      {/* MT-2 — on tape the short form fits; the 4 × 6 has room for the host. */}
      <span>{stock === 'l46'
        ? String(tag.url || '').replace(/^https?:\/\//, '')
        : `m/${tag.code}`}</span>
    </div>
  );

  return (
    <div className={`lbl ${stock}`} data-testid={`tag-label-${tag.code}`}>
      {stock === 'l46' ? <div className="stack">{head}</div> : head}
      <div className="qr">
        <QR value={tag.url} size={stock === 'l46' ? 118 : 200} />
      </div>
      {foot}
    </div>
  );
};

/**
 * MT-2 — the preview of the labels, and the button that fetches the real
 * thing. The label itself is a PDF drawn on the server at the exact size of
 * the stock; the printing is done from the PDF viewer, not the browser.
 */
export const TagSheet = ({ projectId, tags, stock, firm, project, projectCode,
                           onDone, onBack }) => {
  const [busy, setBusy] = useState(false);
  // MT-3 — the tape has no height until the words are laid out, so the preview
  // says what the strip will measure.
  const [pageMm, setPageMm] = useState(null);
  const sheetRef = useRef(null);

  // PR-1b — one step from here too: the print box, not the viewer.
  const print = async () => {
    setBusy(true);
    await printPdf(`${API}/projects/${projectId}/material-tags/print.pdf`,
                   { params: { ids: tags.map((t) => t.id).join(','), stock } });
    setBusy(false);
  };

  // MT-3 — the tape has no height until the words are laid out, so the caption
  // reads the page the printer will be sent, straight off the PDF's MediaBox.
  // The browser wraps a line or two differently from the print engine, so the
  // measured preview is only the fallback.
  useEffect(() => {
    let alive = true;
    const measure = () => {
      const label = sheetRef.current?.querySelector('.lbl');
      if (!label || !alive) return;
      const box = label.getBoundingClientRect();
      const perMm = 96 / 25.4;
      setPageMm({ w: Math.round(box.width / perMm),
                  h: Math.ceil(box.height / perMm) });
    };
    measure();
    (async () => {
      try {
        const ids = tags.map((t) => t.id).join(',');
        const r = await axios.get(
          `${API}/projects/${projectId}/material-tags/print.pdf`,
          { params: { ids, stock }, responseType: 'arraybuffer' });
        // ReportLab writes the page object after the fonts, so the whole
        // file is read; it is one small label, not a drawing set.
        const bytes = new Uint8Array(r.data);
        let text = '';
        for (let i = 0; i < bytes.length; i += 8192) {
          text += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
        }
        const box = text.match(/MediaBox\s*\[\s*0\s+0\s+([\d.]+)\s+([\d.]+)/);
        if (box && alive) {
          setPageMm({ w: Math.round(box[1] / 72 * 25.4),
                      h: Math.round(box[2] / 72 * 25.4) });
        }
      } catch { /* the measured preview stands */ }
    })();
    return () => { alive = false; };
  }, [tags, stock, projectId]);

  useEffect(() => {
    document.body.dataset.kmPrint = 'material-tag';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/styles/material-tag.css';
    link.dataset.kmTagStyles = 'true';
    document.head.appendChild(link);
    return () => {
      delete document.body.dataset.kmPrint;
      link.remove();
    };
  }, []);

  return (
    <div className="kmt-modal" data-testid="material-tag-sheet-modal">
      <div className="km-doc" data-testid="material-tag-sheet" ref={sheetRef}>
        {tags.map((tag) => (
          <TagLabel key={tag.code} tag={tag} stock={stock}
            firm={firm} project={project} projectCode={projectCode} />
        ))}
      </div>
      {pageMm ? (
        <div className="w" data-km-noprint
          style={{ justifyContent: 'center', fontSize: 12, opacity: 0.7 }}
          data-testid="tag-sheet-page-size">
          {pageMm.w} × {pageMm.h} mm
        </div>
      ) : null}
      <div className="w" data-km-noprint style={{ justifyContent: 'center', gap: 8 }}>
        <button type="button" className="kso-btn brand" onClick={print}
          disabled={busy} data-testid="tag-sheet-print">
          {busy ? 'Printing…' : 'Print label'}</button>
        {onBack ? (
          <button type="button" className="kso-tag" onClick={onBack}
            data-testid="print-label-back">Back</button>
        ) : null}
        <button type="button" className="kso-btn" onClick={onDone}
          data-testid="tag-sheet-close">Done</button>
      </div>
    </div>
  );
};

/**
 * DP276-M1/M2, MT-2 — the tag form: everything the label will say, before it
 * prints. Every field is a prefill, not a fact, and the Tag name and note are
 * remembered on the material so the next print starts where this one ended.
 */
export const MaterialTagForm = ({
  projectId, source, body = {}, room = '', roomId = null, location = '', title = '',
  trade = '', tradePrefix = '', count = 1, maxCount = 50, onClose, onMade,
  tagName = '', tagNote = '', imageCount = 0, existingTag = null,
  // SP-2 — Receive opens this with the whole order and the box that landed.
  firstPackage = 0, replaceTag = null,
}) => {
  // MT-2 — a reprint is the same form, filled from the tag it will reprint.
  const [form, setForm] = useState({
    title: existingTag?.title || title,
    location: existingTag?.location ?? location,
    instructions: existingTag?.instructions ?? tagNote,
    trade: existingTag?.trade || trade,
    tag_name: existingTag?.tag_name ?? tagName,
    count_total: existingTag?.count_total || count,
    room: existingTag?.room || room || '',
    room_id: existingTag?.room_id || roomId || null,
  });
  const { stock, setStock, firmDefault, makeDefault, remember } = useLabelStock(
    existingTag?.stock || '');
  const [showPictures, setShowPictures] = useState(
    existingTag ? !!existingTag.show_pictures : imageCount > 1);
  const [sameLocation, setSameLocation] = useState(true);
  const [spots, setSpots] = useState([]);
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  // SP-2 — one tag per item, or one per box with what each box carries.
  const [packed, setPacked] = useState(
    firstPackage > 0 || Number(replaceTag?.package_count) > 0);
  const [boxes, setBoxes] = useState(() => {
    const one = Number(replaceTag?.package_count) || firstPackage;
    return one > 0 ? [String(one)] : ['1'];
  });
  const [firm, setFirm] = useState({ companyName: '', logoUrl: '' });
  const [project, setProject] = useState({ name: '', project_code: '' });

  useEffect(() => {
    axios.get(`${API}/projects/${projectId}`)
      .then((r) => setProject({ name: r.data?.name || '', project_code: r.data?.project_code || '' }))
      .catch(() => setProject({ name: '', project_code: '' }));
    axios.get(`${API}/custom-lists`)
      .then((r) => setTrades((r.data?.trades || []).map(
        (t) => (typeof t === 'string' ? { name: t, spec_codes: [] } : t))))
      .catch(() => setTrades([]));
    loadTenantBrand().then(setFirm);
  }, [projectId]);

  // SP-1 — the trade this label starts with: the one this material was last
  // printed for, else the category's default trade as Admin has set it. It is
  // a prefill like every other field on this form, and always changeable.
  useEffect(() => {
    if (form.trade || !tradePrefix) return;
    let alive = true;
    axios.get(`${API}/spec-categories/${tradePrefix}/default-trade`)
      .then((r) => {
        const picked = r.data?.trade || '';
        if (alive && picked) setForm((f) => (f.trade ? f : { ...f, trade: picked }));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [tradePrefix, form.trade]);

  const n = Math.max(1, Math.min(Number(form.count_total) || 1, maxCount));
  const sizes = boxes.map((size) => Math.max(0, Number(size) || 0));
  const packedSum = sizes.reduce((a, b) => a + b, 0);
  const packsAdd = !packed || !!replaceTag || packedSum === n;
  const roomName = form.room || '';

  const perTag = useMemo(
    () => Array.from({ length: n }, (_, i) => spots[i] ?? form.location),
    [n, spots, form.location]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setSpot = (index) => (e) => {
    const next = [...perTag];
    next[index] = e.target.value;
    setSpots(next);
  };

  // PR-1b — printing is one step: the tags are made and the print box opens
  // on them. Preview is the way to see the face first, for anyone who wants it.
  const made = async (tags, preview) => {
    onMade?.(tags);
    if (preview) { setSheet(tags); return; }
    await printPdf(`${API}/projects/${projectId}/material-tags/print.pdf`,
                   { params: { ids: tags.map((t) => t.id).join(','), stock } });
    onClose?.();
  };

  const print = async (preview = false) => {
    if (!form.title.trim() || !roomName.trim() || !packsAdd) return;
    // SP-2 — Edit & reprint: the corrected face is a new tag, and the old one
    // is marked as the tag it replaced.
    if (replaceTag) {
      try {
        setBusy(true);
        const fresh = await axios.post(
          `${API}/projects/${projectId}/material-tags/${replaceTag.id}/replace`, {
            title: form.title.trim(),
            room: roomName.trim(),
            room_id: form.room_id || null,
            location: form.location.trim(),
            tag_name: form.tag_name.trim(),
            instructions: form.instructions.trim(),
            trade: form.trade.trim(),
            show_pictures: showPictures,
            ...(packed ? { package_count: sizes[0] || 0 } : {}),
            stock,
          });
        await remember(stock);
        await made(fresh.data.tags, preview);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'That tag did not reprint');
      } finally { setBusy(false); }
      return;
    }
    // MT-2 — the reprint corrects the tag it was opened from and keeps its code.
    if (existingTag) {
      try {
        setBusy(true);
        const fixed = await axios.patch(
          `${API}/projects/${projectId}/material-tags/${existingTag.id}`, {
            title: form.title.trim(),
            room: roomName.trim(),
            room_id: form.room_id || null,
            location: form.location.trim(),
            tag_name: form.tag_name.trim(),
            instructions: form.instructions.trim(),
            show_pictures: showPictures,
            trade: form.trade.trim(),
            stock,
          });
        await remember(stock);
        await made([{ ...existingTag, ...(fixed.data || {}) }], preview);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'That tag did not reprint');
      } finally { setBusy(false); }
      return;
    }
    try {
      setBusy(true);
      const fresh = await axios.post(`${API}/projects/${projectId}/material-tags`, {
        source, ...body,
        title: form.title.trim(),
        room: roomName.trim(),
        room_id: form.room_id || null,
        tag_name: form.tag_name.trim(),
        instructions: form.instructions.trim(),
        show_pictures: showPictures,
        trade: form.trade.trim(),
        count_total: n,
        ...(packed ? { packages: sizes } : {}),
        stock,
        ...(n > 1 && !sameLocation
          ? { locations: perTag.map((spot) => (spot || '').trim()) }
          : { location: form.location.trim() }),
      });
      const tags = fresh.data.tags;
      await remember(stock);
      await made(tags, preview);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Those tags did not print');
    } finally { setBusy(false); }
  };

  if (sheet) {
    return (
      <TagSheet projectId={projectId} tags={sheet} stock={stock} firm={firm}
        project={project.name} projectCode={project.project_code}
        onBack={() => setSheet(null)}
        onDone={() => { setSheet(null); onClose?.(); }} />
    );
  }

  return (
    <div className="kmt-modal" data-testid="material-tag-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="kso-card" data-testid="material-tag-form">
      <div className="kso-card__hd">
        {existingTag ? `Reprint tag ${existingTag.code}` : 'Print label'}
        <span className="n">{existingTag ? 'same code' : `${n} tag${n > 1 ? 's' : ''}`}</span>
      </div>
      <div className="kso-card__bd">
        <div className="kmt-form" data-km-noprint>
          <label>Room
            <RoomPicker projectId={projectId} className="kso-in"
              value={{ id: form.room_id || null, name: form.room || '' }}
              onChange={(r) => setForm({ ...form, room: r.name || '', room_id: r.id || null })}
              placeholder="Which room?"
              testid="tag-room" />
          </label>
          <label>Location
            <input className="kso-in" value={form.location} onChange={set('location')}
              placeholder="north wall, left of the window" data-testid="tag-location" />
          </label>
          <label style={{ gridColumn: '1 / -1' }}>Tag name
            <input className="kso-in" value={form.tag_name} onChange={set('tag_name')}
              placeholder="Shower valve" data-testid="tag-name" />
            <span className="kso-hint" style={{ margin: 0 }}>
              what it is, in a word or two — this prints largest
            </span>
          </label>
          <label style={{ gridColumn: '1 / -1' }}>Note
            <input className="kso-in" value={form.instructions} onChange={set('instructions')}
              placeholder="hold 42 in. to centreline; do not cut the tile"
              data-testid="tag-note" />
            <span className="kso-hint" style={{ margin: 0 }}>
              prints on the tag and shows on the scan
            </span>
          </label>
          {imageCount > 1 || existingTag ? (
            <div className="w" style={{ gridColumn: '1 / -1' }}>
              <label className="kfs-sw">
                <input type="checkbox" checked={showPictures}
                  onChange={(e) => setShowPictures(e.target.checked)}
                  data-testid="tag-show-pictures" />
                <span className={`t${showPictures ? ' on' : ''}`} />
                Show the clip&apos;s pictures on the scan{imageCount ? ` (${imageCount})` : ''}
              </label>
            </div>
          ) : null}
          <label>How many
            {existingTag ? (
              <input className="kso-in" value="Reprint" readOnly
                data-testid="tag-count" />
            ) : (
              <input className="kso-in" type="number" min="1" max={maxCount}
                value={form.count_total} onChange={set('count_total')}
                data-testid="tag-count" />
            )}
          </label>
          {!existingTag || replaceTag ? (
            <label style={{ gridColumn: '1 / -1' }}>How packed
              <div className="ksh-seg" data-testid="print-label-packed">
                <span className={packed ? '' : 'on'} onClick={() => setPacked(false)}
                  data-testid="print-label-per-item">One tag per item</span>
                <span className={packed ? 'on' : ''}
                  onClick={() => { setPacked(true); if (!boxes.length) setBoxes(['1']); }}
                  data-testid="print-label-packages">By package</span>
              </div>
              {packed ? (
                <div className="kmt-boxes" data-testid="print-label-box-row">
                  {boxes.map((size, index) => (
                    <input key={index} className="kso-in" type="number" min="1"
                      value={size}
                      onChange={(e) => setBoxes(boxes.map(
                        (old, i) => (i === index ? e.target.value : old)))}
                      data-testid={`print-label-package-${index + 1}`} />
                  ))}
                  {!replaceTag ? (
                    <button type="button" className="kso-tag"
                      onClick={() => setBoxes([...boxes, '1'])}
                      data-testid="print-label-add-box">Add a box</button>
                  ) : null}
                  <button type="button" className="kso-tag" disabled={boxes.length < 2}
                    onClick={() => setBoxes(boxes.slice(0, -1))}
                    data-testid="print-label-drop-box">One fewer</button>
                </div>
              ) : null}
              {packed ? (
                <span className={`kso-hint${packsAdd ? '' : ' is-late'}`}
                  style={{ margin: 0 }} data-testid="print-label-packed-sum">
                  {`${packedSum} of ${n} packed`}
                </span>
              ) : null}
            </label>
          ) : null}
          <label style={{ gridColumn: '1 / -1' }}>Label stock
            <div className="ksh-seg" data-testid="tag-stock">
              {LABEL_STOCKS.map(([id, label]) => (
                <span key={id} className={stock === id ? 'on' : ''}
                  onClick={() => setStock(id)} data-testid={`tag-stock-${id}`}>{label}</span>
              ))}
            </div>
            <button type="button" className="kso-tag" onClick={makeDefault}
              disabled={stock === firmDefault} data-testid="tag-stock-default">
              {stock === firmDefault ? 'Your default' : 'Make this the default'}
            </button>
          </label>
          {n > 1 ? (
            <div className="w">
              <label className="kfs-sw">
                <input type="checkbox" checked={sameLocation}
                  onChange={(e) => setSameLocation(e.target.checked)}
                  data-testid="tag-same-location" />
                <span className={`t${sameLocation ? ' on' : ''}`} />
                Same location for all
              </label>
            </div>
          ) : null}
          {n > 1 && !sameLocation ? perTag.map((spot, index) => (
            <label key={index}>{index + 1} of {n}
              <input className="kso-in" value={spot} onChange={setSpot(index)}
                placeholder="where this one goes"
                data-testid={`tag-location-${index + 1}`} />
            </label>
          )) : null}
          <label style={{ gridColumn: '1 / -1' }}>What is it
            <input className="kso-in" value={form.title} onChange={set('title')}
              placeholder="Shower door hardware, box 2" data-testid="tag-title" />
          </label>
          <label>Trade
            <select className="kso-in" value={form.trade} onChange={set('trade')}
              data-testid="print-label-trade">
              <option value="">Which trade?</option>
              {trades.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
              {form.trade && !trades.some((t) => t.name === form.trade)
                ? <option value={form.trade}>{form.trade}</option> : null}
            </select>
          </label>
          <div className="w" data-km-noprint>
            <button type="button" className="kso-btn brand" disabled={busy || !packsAdd}
              onClick={() => print(false)} data-testid="print-label-print">
              {existingTag || replaceTag
                ? `Reprint ${(existingTag || replaceTag).code}`
                : packed
                  ? `Print ${boxes.length} label${boxes.length > 1 ? 's' : ''}`
                  : `Print ${n} tag${n > 1 ? 's' : ''}`}
            </button>
            <button type="button" className="kso-tag" disabled={busy || !packsAdd}
              onClick={() => print(true)} data-testid="print-label-preview">
              Preview
            </button>
            <button type="button" className="kso-btn" onClick={onClose}
              data-testid="tag-cancel">Cancel</button>
            <span className="kso-hint" style={{ margin: 0 }}>
              {existingTag
                ? `This material already has a tag — corrections reprint ${existingTag.code}.`
                : 'Tag name, note and trade are remembered on this material for the next print.'}
            </span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MaterialTagForm;
